import { useEffect, useMemo, useRef, useState } from "react";
import { MemberService } from "@/services/MemberService";
import type { MessageableEntity } from "@/types/MessageableEntity";

/**
 * Hook for managing new conversation creation
 * Handles member and group search, selection, and message sending
 */
export function useNewConversation(
  onSendMessage: (params: {
    type: "member" | "group";
    recipientId?: string;
    locationIds?: string[];
    content: string;
  }) => Promise<void>,
  initialEntity?: MessageableEntity | null
) {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchResults, setSearchResults] = useState<MessageableEntity[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<MessageableEntity | null>(null);
  const [message, setMessage] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const hasSetInitialEntity = useRef(false);

  const memberService = useMemo(() => new MemberService(), []);

  // Set initial entity once when provided
  useEffect(() => {
    if (initialEntity && !hasSetInitialEntity.current) {
      setSelectedEntity(initialEntity);
      hasSetInitialEntity.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEntity?.id]); // Only re-run if the entity ID changes, not the whole object

  // Search for entities (members + groups) the user can message with debounce
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);

    const handler = setTimeout(() => {
      memberService
        .getMembersICanMessage(searchTerm)
        .then((entities) => {
          setSearchResults(entities);
          setIsSearching(false);
        })
        .catch((error) => {
          console.error("Failed to search:", error);
          setSearchResults([]);
          setIsSearching(false);
        });
    }, 300); // debounce 300ms

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleEntitySelect = (entity: MessageableEntity) => {
    setSelectedEntity(entity);
    setSearchTerm("");
    setSearchResults([]);
  };

  const handleRemoveSelection = () => {
    setSelectedEntity(null);
  };

  const handleSend = async () => {
    if (!selectedEntity || !message.trim()) return false;

    setIsSending(true);
    try {
      if (selectedEntity.type === "group") {
        await onSendMessage({
          type: "group",
          locationIds: selectedEntity.locationIds,
          content: message,
        });
      } else {
        await onSendMessage({
          type: "member",
          recipientId: selectedEntity.id,
          content: message,
        });
      }
      return true; // Success
    } catch (error) {
      console.error("Failed to send message:", error);
      return false; // Failure
    } finally {
      setIsSending(false);
    }
  };

  const resetState = () => {
    setSelectedEntity(null);
    setMessage("");
    setSearchTerm("");
    setSearchResults([]);
    setIsSearching(false);
    setIsSending(false);
    hasSetInitialEntity.current = false;
  };

  const isSendDisabled = !selectedEntity || !message.trim() || isSending;

  return {
    // State
    searchTerm,
    searchResults,
    selectedEntity,
    message,
    isSearching,
    isSending,
    isSendDisabled,

    // Actions
    setSearchTerm,
    setMessage,
    handleEntitySelect,
    handleRemoveSelection,
    handleSend,
    resetState,
  };
}
